// 10-second visual intro tutorial. Four auto-advancing panels, each
// ~2.5 seconds, showing the core loop visually rather than in text.
// Replaces the older TutorialOverlay class body.
//
// Storage: `smp.introSeenV1` — when present, `start(false)` no-ops so
// returning players don't see it again. `start(true)` (from the pause
// menu "Replay Tutorial" button) forces it to run.

import { ensureY2kStyle, isY2kActive } from './y2k_style';

const INTRO_KEY = 'smp.introSeenV1';
const PANEL_MS = 2500;
const PANEL_COUNT = 4;

export class IntroTutorial {
  private root: HTMLDivElement;
  private parent: HTMLElement;
  private panel = 0;
  private openState = false;
  private autoTimer: ReturnType<typeof setTimeout> | null = null;
  private onKey: ((e: KeyboardEvent) => void) | null = null;
  private cleanup: Array<() => void> = [];

  constructor(parent: HTMLElement) {
    this.parent = parent;
    // Mount shared y2k CSS if this session is on the flagship theme —
    // unlocks .y2k-heading + .y2k-star classes used in the panels.
    if (isY2kActive()) ensureY2kStyle();
    this.root = document.createElement('div');
    this.root.className = 'ui interactive';
    const y2k = isY2kActive();
    this.root.style.cssText = `
      position: fixed; inset: 0;
      z-index: 210;
      display: none;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: ${y2k
        ? 'radial-gradient(ellipse at 50% 110%, #fff20022 0%, #ff149344 12%, #7a0a6acc 35%, #2a0a4aee 60%, #05001afa 100%)'
        : 'radial-gradient(circle at center, rgba(15,23,42,.95), rgba(2,6,23,.99))'};
      font: 700 14px system-ui, sans-serif;
      color: #e5e7eb;
      overflow: hidden;
    `;
    parent.appendChild(this.root);
    if (y2k) this.mountY2kChrome();
  }

  // Corner starbursts + scanline overlay — y2k only.
  private mountY2kChrome(): void {
    for (const [top, left, right, bottom, size] of [
      [ '18px', '22px', '',    '',    '28px' ],
      [ '24px', '',    '24px', '',    '22px' ],
      [ '',     '28px', '',    '24px','20px' ],
      [ '',     '',    '28px', '28px','24px' ],
    ] as const) {
      const star = document.createElement('div');
      star.className = 'y2k-star';
      star.style.cssText = `
        position: absolute;
        ${top    ? `top:${top};`       : ''}
        ${left   ? `left:${left};`     : ''}
        ${right  ? `right:${right};`   : ''}
        ${bottom ? `bottom:${bottom};` : ''}
        width:${size}; height:${size};
        pointer-events: none;
      `;
      this.root.appendChild(star);
    }
    const scan = document.createElement('div');
    scan.style.cssText = `
      position: absolute; inset: 0;
      background: repeating-linear-gradient(180deg, transparent 0 2px, rgba(255,255,255,.04) 2px 3px);
      pointer-events: none;
      z-index: 0;
    `;
    this.root.appendChild(scan);
  }

  start(force = false): void {
    if (!force && localStorage.getItem(INTRO_KEY) === '1') return;
    if (this.openState) return;
    this.openState = true;
    this.panel = 0;
    this.root.style.display = 'flex';
    this.render();
    this.startAutoAdvance();
    this.bindKeys();
  }

  isOpen(): boolean {
    return this.openState;
  }

  private close(): void {
    if (!this.openState) return;
    this.openState = false;
    localStorage.setItem(INTRO_KEY, '1');
    if (this.autoTimer) clearTimeout(this.autoTimer);
    this.autoTimer = null;
    if (this.onKey) removeEventListener('keydown', this.onKey);
    this.onKey = null;
    for (const fn of this.cleanup) fn();
    this.cleanup = [];
    this.root.style.display = 'none';
  }

  private advance(delta: number): void {
    const next = this.panel + delta;
    if (next < 0) {
      this.panel = 0;
    } else if (next >= PANEL_COUNT) {
      this.close();
      return;
    } else {
      this.panel = next;
    }
    this.render();
    this.startAutoAdvance();
  }

  private startAutoAdvance(): void {
    if (this.autoTimer) clearTimeout(this.autoTimer);
    this.autoTimer = setTimeout(() => {
      if (this.panel < PANEL_COUNT - 1) {
        this.panel++;
        this.render();
        this.startAutoAdvance();
      }
      // On the last panel, wait for user input (no auto-close).
    }, PANEL_MS);
  }

  private bindKeys(): void {
    this.onKey = (e: KeyboardEvent) => {
      if (!this.openState) return;
      if (e.code === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.code === 'ArrowRight' || e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        this.advance(1);
        return;
      }
      if (e.code === 'ArrowLeft') {
        e.preventDefault();
        this.advance(-1);
        return;
      }
    };
    addEventListener('keydown', this.onKey);
  }

  private render(): void {
    // Clear previous panel cleanups
    for (const fn of this.cleanup) fn();
    this.cleanup = [];

    this.root.innerHTML = '';
    // Starbursts + scanlines get wiped by innerHTML clear — remount.
    if (isY2kActive()) this.mountY2kChrome();

    const y2kSkip = isY2kActive();
    // Skip button (top-right)
    const skip = document.createElement('button');
    skip.textContent = 'SKIP ▸';
    skip.style.cssText = y2kSkip
      ? `
        position: absolute;
        top: 18px; right: 22px;
        padding: 8px 18px;
        background: linear-gradient(145deg, #140738dd, #2a0a4add);
        border: 1px solid #ff149388;
        border-radius: 8px;
        color: #aaffff;
        cursor: pointer;
        font: 900 11px ui-monospace, monospace;
        letter-spacing: 2px;
        text-shadow: 0 0 6px #00f0ff80;
        z-index: 3;
      `
      : `
        position: absolute;
        top: 18px; right: 22px;
        padding: 8px 18px;
        background: rgba(15,23,42,.7);
        border: 1px solid #475569;
        border-radius: 8px;
        color: #94a3b8;
        cursor: pointer;
        font: 800 11px system-ui;
        letter-spacing: 2px;
      `;
    skip.addEventListener('click', () => this.close());
    this.root.appendChild(skip);

    // Panel body container
    const stage = document.createElement('div');
    stage.style.cssText = `
      flex: 1;
      width: 100%;
      max-width: 900px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 32px 20px 12px;
      text-align: center;
      gap: 18px;
    `;
    this.root.appendChild(stage);

    switch (this.panel) {
      case 0: this.renderTribes(stage); break;
      case 1: this.renderFarm(stage); break;
      case 2: this.renderLevelUp(stage); break;
      case 3: this.renderKills(stage); break;
    }

    // Dots indicator
    const dots = document.createElement('div');
    dots.style.cssText = `
      display: flex; gap: 10px;
      margin: 10px 0 16px;
      pointer-events: none;
    `;
    for (let i = 0; i < PANEL_COUNT; i++) {
      const d = document.createElement('div');
      d.style.cssText = `
        width: 10px; height: 10px; border-radius: 50%;
        background: ${i === this.panel ? '#fde047' : 'rgba(255,255,255,.22)'};
        box-shadow: ${i === this.panel ? '0 0 12px #fde04788' : 'none'};
        transition: background .2s;
      `;
      dots.appendChild(d);
    }
    this.root.appendChild(dots);

    // Footer hint
    const hint = document.createElement('div');
    hint.style.cssText = `
      font: 700 10px system-ui, sans-serif;
      letter-spacing: 2px;
      color: rgba(226,232,240,.55);
      margin-bottom: 22px;
    `;
    hint.textContent = this.panel < PANEL_COUNT - 1
      ? '← →  NAVIGATE  ·  SPACE  NEXT  ·  ESC  SKIP'
      : 'SPACE / ENTER TO START  ·  ESC SKIP';
    this.root.appendChild(hint);
  }

  // ---- Panel 1: Two Tribes ----
  private renderTribes(stage: HTMLElement): void {
    const y2k = isY2kActive();
    const virginColor = y2k ? '#00f0ff' : '#60a5fa';
    const infColor = y2k ? '#ff1493' : '#fb923c';
    const vsColor = y2k ? '#fff200' : '#fde047';

    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: center; gap: 40px; flex-wrap: wrap;
      justify-content: center;
      position: relative; z-index: 2;
    `;

    const side = (icon: string, label: string, sub: string, color: string) => {
      const d = document.createElement('div');
      d.style.cssText = `
        display: flex; flex-direction: column; align-items: center; gap: 6px;
      `;
      d.innerHTML = `
        <div style="font-size:96px;line-height:1;filter: drop-shadow(0 0 30px ${color}cc) drop-shadow(0 0 12px ${color});">${icon}</div>
        <div style="font:900 36px system-ui;letter-spacing:3px;color:${color};text-shadow: 0 0 20px ${color}88, 0 0 6px ${color};">${label}</div>
        <div style="font:800 11px system-ui;letter-spacing:3px;opacity:.75;">( ${sub} )</div>
      `;
      return d;
    };

    row.appendChild(side('🥷', 'VIRGIN', 'ninja', virginColor));

    const vs = document.createElement('div');
    if (y2k) {
      vs.className = 'y2k-heading';
      vs.style.cssText = `
        font-size: 72px !important;
        filter: drop-shadow(0 0 22px rgba(255,242,0,.8));
      `;
    } else {
      vs.style.cssText = `
        font: 900 56px system-ui;
        letter-spacing: 4px;
        color: ${vsColor};
        filter: drop-shadow(0 0 18px rgba(253,224,71,.7));
      `;
    }
    vs.textContent = 'VS';
    row.appendChild(vs);

    row.appendChild(side('🎉', 'CHAD', 'streamer', infColor));
    stage.appendChild(row);

    const caption = document.createElement('div');
    caption.style.cssText = `
      font: 800 18px system-ui;
      letter-spacing: 2px;
      color: ${y2k ? '#fff' : '#e5e7eb'};
      text-shadow: ${y2k ? `0 0 8px ${infColor}aa, 0 0 8px ${virginColor}aa` : 'none'};
      margin-top: 12px;
      position: relative; z-index: 2;
    `;
    caption.textContent = 'Pick a side. Fight to 3:00.';
    stage.appendChild(caption);
  }

  // ---- Panel 2: Farm Your Area ----
  private renderFarm(stage: HTMLElement): void {
    const y2k = isY2kActive();
    const title = document.createElement('div');
    if (y2k) {
      title.className = 'y2k-heading';
      title.style.cssText = `font-size: 28px !important; position: relative; z-index: 2;`;
    } else {
      title.style.cssText = `
        font: 900 22px system-ui;
        letter-spacing: 3px;
        color: #60a5fa;
        text-shadow: 0 0 20px #60a5fa55;
      `;
    }
    title.textContent = 'FARM YOUR AREA';
    stage.appendChild(title);

    // Animated canvas diagram
    const canvas = document.createElement('canvas');
    canvas.width = 460;
    canvas.height = 280;
    canvas.style.cssText = 'display:block;max-width:90vw;';
    stage.appendChild(canvas);
    this.cleanup.push(animateFarmDiagram(canvas));

    const caption = document.createElement('div');
    caption.style.cssText = `
      font: 700 15px system-ui;
      color: #e5e7eb;
      max-width: 520px;
      line-height: 1.5;
    `;
    caption.innerHTML = `Stand in your <span style="color:#60a5fa;">weapon zone</span> near NPCs to farm XP.<br>Stay too long — they get mad and <span style="color:#ef4444;">chase you</span>.`;
    stage.appendChild(caption);

    const sub = document.createElement('div');
    sub.style.cssText = `
      font: 800 11px system-ui;
      letter-spacing: 2px;
      opacity: .65;
    `;
    sub.textContent = '3s SAFE  ·  8s ANNOYED  ·  13s COMBAT';
    stage.appendChild(sub);
  }

  // ---- Panel 3: Level Up, Pick Powerups ----
  private renderLevelUp(stage: HTMLElement): void {
    const y2k = isY2kActive();
    const title = document.createElement('div');
    if (y2k) {
      title.className = 'y2k-heading';
      title.style.cssText = `font-size: 28px !important; position: relative; z-index: 2;`;
    } else {
      title.style.cssText = `
        font: 900 22px system-ui;
        letter-spacing: 3px;
        color: #fde047;
        text-shadow: 0 0 20px #fde04755;
      `;
    }
    title.textContent = 'LEVEL UP · PICK 1 OF 3';
    stage.appendChild(title);

    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; gap: 18px; justify-content: center; align-items: flex-end;
      margin-top: 12px;
    `;

    const card = (color: string, rarity: string, delay: number, hi: boolean) => {
      const d = document.createElement('div');
      d.style.cssText = `
        width: 140px; height: 190px;
        background: linear-gradient(180deg, ${color}33, rgba(15,23,42,.92));
        border: 2px solid ${color};
        border-radius: 14px;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        gap: 10px;
        box-shadow: 0 12px 40px ${color}55, inset 0 0 30px ${color}22;
        transform: translateY(-40px) scale(.8);
        opacity: 0;
        animation: introCardDrop 500ms ease-out ${delay}ms forwards;
        ${hi ? 'outline: 3px solid #fde047; outline-offset: 4px;' : ''}
      `;
      d.innerHTML = `
        <div style="font-size:44px;filter:drop-shadow(0 0 14px ${color});">✦</div>
        <div style="font:900 11px system-ui;letter-spacing:2px;color:${color};">${rarity}</div>
        ${hi ? `<div style="font:900 14px system-ui;color:#fde047;margin-top:4px;letter-spacing:1.5px;">PICK ME</div>` : ''}
      `;
      return d;
    };
    if (y2k) {
      row.appendChild(card('#c8d8f0', 'COMMON',   0,   false));
      row.appendChild(card('#4aff8a', 'UNCOMMON', 150, true));
      row.appendChild(card('#00f0ff', 'RARE',     300, false));
    } else {
      row.appendChild(card('#9ca3af', 'COMMON',   0,   false));
      row.appendChild(card('#22c55e', 'UNCOMMON', 150, true));
      row.appendChild(card('#3b82f6', 'RARE',     300, false));
    }
    stage.appendChild(row);

    // Inject keyframe if missing
    if (!document.getElementById('intro-keyframes')) {
      const style = document.createElement('style');
      style.id = 'intro-keyframes';
      style.textContent = `
        @keyframes introCardDrop {
          from { transform: translateY(-40px) scale(.8); opacity: 0; }
          to   { transform: translateY(0)     scale(1);  opacity: 1; }
        }
        @keyframes introKillBob {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-4px); }
        }
      `;
      document.head.appendChild(style);
    }

    const caption = document.createElement('div');
    caption.style.cssText = `
      font: 700 14px system-ui;
      color: #e5e7eb;
      margin-top: 20px;
    `;
    caption.innerHTML = `Every level-up rolls <span style="color:#fde047;">3 random cards</span> — pick one.<br>Stats · Body mods · Weapons. Stack them.`;
    stage.appendChild(caption);

    const keys = document.createElement('div');
    keys.style.cssText = `
      display: flex; gap: 10px; margin-top: 4px;
      font: 900 11px system-ui; letter-spacing: 2px; opacity: .7;
    `;
    keys.innerHTML = `
      <span style="padding:4px 10px;border:1px solid #475569;border-radius:6px;">1</span>
      <span style="padding:4px 10px;border:1px solid #475569;border-radius:6px;">2</span>
      <span style="padding:4px 10px;border:1px solid #475569;border-radius:6px;">3</span>
    `;
    stage.appendChild(keys);
  }

  // ---- Panel 4: Kills Win ----
  private renderKills(stage: HTMLElement): void {
    const y2k = isY2kActive();
    const title = document.createElement('div');
    if (y2k) {
      title.className = 'y2k-heading';
      title.style.cssText = `font-size: 28px !important; position: relative; z-index: 2;`;
    } else {
      title.style.cssText = `
        font: 900 22px system-ui;
        letter-spacing: 3px;
        color: #ef4444;
        text-shadow: 0 0 20px #ef444455;
      `;
    }
    title.textContent = 'MOST KILLS WINS';
    stage.appendChild(title);

    const scoreRow = document.createElement('div');
    scoreRow.style.cssText = `
      display: flex; align-items: center; gap: 28px;
      font: 900 64px system-ui;
      margin-top: 6px;
      position: relative; z-index: 2;
    `;
    if (y2k) {
      scoreRow.innerHTML = `
        <span style="color:#00f0ff;text-shadow:0 0 14px #00f0ff, 0 0 4px #fff;">3</span>
        <span style="font:900 34px system-ui;color:#fff200;letter-spacing:4px;text-shadow:0 0 16px #fff200, 0 0 4px #ff1493;">3:00</span>
        <span style="color:#ff1493;text-shadow:0 0 14px #ff1493, 0 0 4px #fff;">2</span>
      `;
    } else {
      scoreRow.innerHTML = `
        <span style="color:#60a5fa;">3</span>
        <span style="font:900 34px system-ui;color:#fde047;letter-spacing:4px;">3:00</span>
        <span style="color:#fb923c;">2</span>
      `;
    }
    stage.appendChild(scoreRow);

    const skulls = document.createElement('div');
    skulls.style.cssText = `
      display: flex; gap: 14px; font-size: 32px; margin-top: 4px;
    `;
    for (let i = 0; i < 5; i++) {
      const s = document.createElement('span');
      s.textContent = '☠';
      s.style.cssText = `
        animation: introKillBob 1.2s ease-in-out ${i * 120}ms infinite;
        filter: drop-shadow(0 0 8px #ef4444);
      `;
      skulls.appendChild(s);
    }
    stage.appendChild(skulls);

    const caption = document.createElement('div');
    caption.style.cssText = `
      font: 700 15px system-ui;
      color: #e5e7eb;
      max-width: 580px;
      line-height: 1.5;
      margin-top: 14px;
    `;
    caption.innerHTML = `
      Most kills at 3:00 wins. Visit both of your faction venues to summon
      <span style="color:#fb923c;">your boss</span>. Kill the enemy boss to
      drop <span style="color:#f59e0b;">legendary weapons</span>.
    `;
    stage.appendChild(caption);

    const cta = document.createElement('div');
    cta.style.cssText = `
      font: 900 13px system-ui;
      letter-spacing: 3px;
      color: #fde047;
      margin-top: 8px;
      text-shadow: 0 0 14px #fde04755;
    `;
    cta.textContent = 'PRESS SPACE TO PLAY';
    stage.appendChild(cta);
  }
}

// ---- Farm diagram animation ----

function animateFarmDiagram(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d')!;
  let raf = 0;
  let last = performance.now();
  let running = true;
  const W = canvas.width;
  const H = canvas.height;
  const cx = W / 2;
  const cy = H / 2;

  type Npc = { angle: number; radius: number; mood: number; dx: number; dy: number };
  const npcs: Npc[] = [];
  for (let i = 0; i < 6; i++) {
    const ang = Math.random() * Math.PI * 2;
    const rad = 40 + Math.random() * 70;
    npcs.push({
      angle: ang,
      radius: rad,
      mood: Math.random() * 2,
      dx: 0, dy: 0,
    });
  }
  type Xp = { x: number; y: number; vy: number; age: number };
  const xps: Xp[] = [];
  let xpTimer = 0;

  function tick(now: number) {
    if (!running) return;
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;

    ctx.clearRect(0, 0, W, H);

    // Ring band (weapon zone)
    const inner = 50;
    const outer = 120;
    ctx.fillStyle = 'rgba(96,165,250,.16)';
    ctx.beginPath();
    ctx.arc(cx, cy, outer, 0, Math.PI * 2);
    ctx.arc(cx, cy, inner, 0, Math.PI * 2, true);
    ctx.fill();
    ctx.strokeStyle = 'rgba(96,165,250,.8)';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, outer, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, inner, 0, Math.PI * 2); ctx.stroke();

    // Player dot
    ctx.fillStyle = '#93c5fd';
    ctx.beginPath(); ctx.arc(cx, cy, 8, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 11px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('🥷', cx, cy - 14);

    // NPCs — slow drift, mood rises over time
    for (const n of npcs) {
      n.mood += dt * 0.15;
      n.angle += dt * 0.25;
      const px = cx + Math.cos(n.angle) * n.radius;
      const py = cy + Math.sin(n.angle) * n.radius;
      // Color ramps green→yellow→red
      const moodFrac = Math.min(1, n.mood / 6);
      const r = Math.floor(90 + moodFrac * 150);
      const g = Math.floor(180 - moodFrac * 140);
      const b = Math.floor(80 - moodFrac * 50);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI * 2); ctx.fill();
      if (n.mood > 5) {
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }

    // +XP popups
    xpTimer -= dt;
    if (xpTimer <= 0) {
      xpTimer = 0.35;
      const pickAngle = Math.random() * Math.PI * 2;
      const pickR = inner + Math.random() * (outer - inner);
      xps.push({
        x: cx + Math.cos(pickAngle) * pickR,
        y: cy + Math.sin(pickAngle) * pickR,
        vy: -20,
        age: 0,
      });
    }
    for (let i = xps.length - 1; i >= 0; i--) {
      const p = xps[i];
      p.age += dt;
      p.y += p.vy * dt;
      if (p.age > 1.2) { xps.splice(i, 1); continue; }
      ctx.fillStyle = `rgba(253,224,71,${1 - p.age / 1.2})`;
      ctx.font = 'bold 14px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('+XP', p.x, p.y);
    }

    raf = requestAnimationFrame(tick);
  }
  raf = requestAnimationFrame(tick);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
  };
}
